// ============================================================
// ✅ App.js - GestCongés
// Configuration des routes et protection par rôle
// Spécial client : Manager = mêmes accès que Admin
// Observateur = accès limité à /menu uniquement
// ============================================================

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import './components/design-system.css';

// 🔐 Authentification
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';

// 🏠 Menu principal
import MenuPage from './pages/MenuPage';

// 🏢 Pages accessibles à tous
import CongesPage from './pages/CongesPage';

// 🏢 Pages SuperAdmin
import SocietesListe from './pages/societes';
import PaysListe from './pages/pays';

// 👥 Gestion du personnel (admin + manager)
import EmployesListe from './pages/employes';
import DepartementsListe from './pages/departements';
import ServicesListe from './pages/services';
import PostesListe from './pages/postes';

// 🏖️ Gestion des congés (admin + manager)
import TypesCongeListe from './pages/typesconge';
import FeriesPage from './pages/feries';

// 📋 Demandes de congés (tous sauf observateur)
import DemandesConge from './pages/demandes';

// ✅ Validations (manager + admin)
import ValidationsPage from './pages/validations';

// 💰 Soldes et fin d'année (admin + manager)
import SaisieJoursPris from './pages/SaisieJoursPris';
import RemplissageSoldesPage from './pages/RemplissageSoldesPage';
import TraitementFinAnneePage from './pages/TraitementFinAnneePage';

// 📅 Saisie rétroactive (admin + manager)
import SaisieRetroactive from './pages/SaisieRetroactive';

// ⚙️ Paramétrage des règles (admin + manager)
import ParametragePage from './pages/parametrage';

// 🗺️ Map & Loi (superadmin)
import MapLoi from './pages/MapLoi';

// 📊 Analyse & KPIs (admin + manager)
import Analyse from './pages/Analyse';

// 📦 Composants partagés
import MainLayout from './layouts/MainLayout';
import { useUser } from './contexts/UserContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// ============================================================
// 🔒 COMPOSANT DE PROTECTION DES ROUTES
// Vérifie l'authentification et le rôle
// ============================================================
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user } = useUser();
  
  // Pas connecté → redirection login
  if (!user) return <Navigate to="/" replace />;
  
  // Rôle non autorisé → redirection menu
  if (allowedRoles && !allowedRoles.includes(user.Role)) return <Navigate to="/menu" replace />;
  
  return children;
};

// ============================================================
// 🎨 WRAPPER DE LAYOUT
// Englobe les pages dans le layout principal
// ============================================================
const withLayout = (component, user) => (
  <MainLayout user={user}>{component}</MainLayout>
);

// ============================================================
// 🚀 COMPOSANT PRINCIPAL
// ============================================================
export default function App() {
  const { user, setUser } = useUser();
  const isObservateur = user?.Role === 'observateur';

  return (
    <>
      <Routes>
        
        {/* ================================================ */}
        {/* 🔐 AUTHENTIFICATION (sans layout)                */}
        {/* ================================================ */}
        <Route path="/" element={<LoginPage onLogin={setUser} />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />

        {/* ================================================ */}
        {/* 🏠 MENU (tous les utilisateurs, y compris observateur) */}
        {/* ================================================ */}
        <Route path="/menu" element={withLayout(<MenuPage />, user)} />
        <Route path="/conges" element={withLayout(<CongesPage />, user)} />
        
        {/* ================================================ */}
        {/* 📊 ANALYSE & KPIs (admin + manager)              */}
        {/* ================================================ */}
        <Route path="/bi" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<Analyse />, user)}
          </ProtectedRoute>
        } />

        {/* ================================================ */}
        {/* 📋 DEMANDES (tous SAUF observateur)              */}
        {/* ================================================ */}
        <Route path="/demandes" element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'user']}>
            {withLayout(<DemandesConge />, user)}
          </ProtectedRoute>
        } />

        {/* ================================================ */}
        {/* ✅ VALIDATIONS (manager + admin)                  */}
        {/* ================================================ */}
        <Route path="/validations" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<ValidationsPage />, user)}
          </ProtectedRoute>
        } />

        {/* ================================================ */}
        {/* 🌍 SUPER ADMIN : Sociétés, Pays, Map & Loi       */}
        {/* ================================================ */}
        <Route path="/societes" element={
          <ProtectedRoute allowedRoles={['admin']}>
            {withLayout(<SocietesListe />, user)}
          </ProtectedRoute>
        } />

        <Route path="/pays" element={
          <ProtectedRoute allowedRoles={['admin']}>
            {withLayout(<PaysListe />, user)}
          </ProtectedRoute>
        } />

        <Route path="/map-loi" element={
          <ProtectedRoute allowedRoles={['admin']}>
            {withLayout(<MapLoi />, user)}
          </ProtectedRoute>
        } />

        {/* ================================================ */}
        {/* 👥 GESTION DU PERSONNEL (admin + manager)        */}
        {/* ================================================ */}
        <Route path="/employes" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<EmployesListe />, user)}
          </ProtectedRoute>
        } />
        <Route path="/departements" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<DepartementsListe />, user)}
          </ProtectedRoute>
        } />
        <Route path="/services" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<ServicesListe />, user)}
          </ProtectedRoute>
        } />
        <Route path="/postes" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<PostesListe />, user)}
          </ProtectedRoute>
        } />

        {/* ================================================ */}
        {/* 🏖️ CONGÉS (admin + manager)                     */}
        /* ================================================ */}
        <Route path="/typesconge" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<TypesCongeListe />, user)}
          </ProtectedRoute>
        } />
        <Route path="/feries" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<FeriesPage />, user)}
          </ProtectedRoute>
        } />

        {/* ================================================ */}
        {/* 💰 SOLDES & TRAITEMENTS (admin + manager)        */}
        /* ================================================ */}
        <Route path="/saisie-jours-pris" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<SaisieJoursPris />, user)}
          </ProtectedRoute>
        } />

        <Route path="/remplissage-soldes" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<RemplissageSoldesPage />, user)}
          </ProtectedRoute>
        } />
        <Route path="/traitement-fin-annee" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<TraitementFinAnneePage />, user)}
          </ProtectedRoute>
        } />

        {/* ================================================ */}
        {/* 📅 SAISIE RÉTROACTIVE (admin + manager)          */}
        {/* ================================================ */}
        <Route path="/saisie-retroactive" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<SaisieRetroactive />, user)}
          </ProtectedRoute>
        } />

        {/* ================================================ */}
        {/* ⚙️ PARAMÉTRAGE (admin + manager)                 */}
        {/* ================================================ */}
        <Route path="/parametrage" element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            {withLayout(<ParametragePage />, user)}
          </ProtectedRoute>
        } />
        
      </Routes>

      {/* ================================================ */}
      {/* 🔔 NOTIFICATIONS GLOBALES (toasts)               */}
      {/* ================================================ */}
      <ToastContainer 
        position="top-right" 
        autoClose={3000} 
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
}